import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import {
  Grid,
  Card,
  Box,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Button,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Divider,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import ScienceIcon from '@mui/icons-material/Science';
import HistoryIcon from '@mui/icons-material/History';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import Swal from 'sweetalert2';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import LoaderOverlay from 'components/LoaderOverlay';
import RichTextEditor from 'components/RichTextEditor';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import MDDatePicker from 'components/MDDatePicker';
import { api } from '../../api';
import { formatDateForDisplay } from '../../utils/dateUtils';

const GENERIC_FIELD_SLUGS = {
  patient_history: 'history-common-params-previous-history',
  family_history: 'history-common-params-family-history',
  allergic_to_drugs: 'history-common-params-allergic-to-drug',
  drug_medical_history: 'history-common-params-drug-medical-history',
  previous_infertility_treatment: 'history-common-params-prev-infertility-treatment',
  notes: 'history-common-params-note',
};

const defaultGenericValues = {
  patient_history: '',
  family_history: '',
  allergic_to_drugs: '',
  drug_medical_history: '',
  previous_infertility_treatment: '',
  notes: '',
};

const defaultSurgicalValues = { surgicalHistories: [{ surgery: '', notes: '', history_date: '' }] };

const logoColor = '#00798E';

function DonorContactHistories() {
  const { donorCode } = useParams();
  const navigate = useNavigate();

  const [donorInfo, setDonorInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [opdParams, setOpdParams] = useState([]);
  const [surgicalMember, setSurgicalMember] = useState(null);
  const [previousSurgicalData, setPreviousSurgicalData] = useState([]);
  const [editingSurgicalRecord, setEditingSurgicalRecord] = useState(null);
  const [originalGenericHistories, setOriginalGenericHistories] = useState([]);
  const [expandedAccordion, setExpandedAccordion] = useState('generic');

  const {
    control: genericControl,
    handleSubmit: handleGenericSubmit,
    reset: resetGeneric,
  } = useForm({ defaultValues: defaultGenericValues });

  const {
    control: surgicalControl,
    handleSubmit: handleSurgicalSubmit,
    reset: resetSurgical,
  } = useForm({ defaultValues: defaultSurgicalValues });

  const { fields: surgicalFields, append: appendSurgical, remove: removeSurgical } = useFieldArray({
    control: surgicalControl,
    name: 'surgicalHistories',
  });

  const loadContactHistory = useCallback(async (contactId) => {
    try {
      const res = await api.opd.getContactHistory(contactId);
      const histories = res?.data?.contact_histories || [];

      // Populate generic form
      const formData = { ...defaultGenericValues };
      const genericRecs = [];
      histories.forEach((h) => {
        const slug = h.param_group_member?.slug;
        Object.entries(GENERIC_FIELD_SLUGS).forEach(([field, fieldSlug]) => {
          if (slug === fieldSlug) {
            formData[field] = h.value || '';
            genericRecs.push({
              id: h.id,
              fieldName: field,
              slug: fieldSlug,
              value: h.value || '',
              contact_id: h.irm_contacts_id,
              param_group_id: h.irm_param_groups_id,
              param_group_member_id: h.irm_param_group_members_id,
            });
          }
        });
      });
      setOriginalGenericHistories(genericRecs);
      resetGeneric(formData);

      // Surgical history
      const surgicalData = histories
        .filter((h) => h.param_group_member?.slug === 'primary-history-common-params-surgical-history')
        .map((h) => ({
          id: h.id,
          surgery: h.param_group_member_option?.value || h.value || '',
          notes: h.remarks || '',
          history_date: h.history_date || '',
          contact_id: h.irm_contacts_id || contactId,
          param_group_id: h.irm_param_groups_id,
          param_group_member_id: h.irm_param_group_members_id,
          param_group_member_option_id: h.irm_param_group_member_options_id || null,
        }));
      setPreviousSurgicalData(surgicalData);
    } catch (err) {
      if (err?.response?.status !== 404) {
        console.error('Error loading contact history', err);
      }
    }
  }, [resetGeneric]);

  useEffect(() => {
    const loadData = async () => {
      if (!donorCode) return;
      setLoading(true);
      try {
        const donorRes = await api.contacts.getDonorDetails(donorCode);
        const rawDonor = donorRes?.data?.donor || donorRes?.data;
        if (!rawDonor) {
          Swal.fire({ icon: 'error', title: 'Not Found', text: 'Donor not found' });
          navigate('/donor-list');
          return;
        }
        const info = {
          id: rawDonor.id,
          name: [rawDonor.first_name, rawDonor.middle_name, rawDonor.last_name].filter(Boolean).join(' '),
          external_code: rawDonor.external_code,
          gender: rawDonor.gender,
        };
        setDonorInfo(info);

        // Load OPD params for surgical history dropdown
        const paramsRes = await api.opd.getHistoryParams();
        if (paramsRes?.data?.primary_history_params?.param_group_members) {
          const surgicalMember = paramsRes.data.primary_history_params.param_group_members.find(
            (m) => m.name === 'Surgical History',
          );
          setSurgicalMember(surgicalMember || null);
          if (surgicalMember?.param_group_member_options) {
            setOpdParams(
              surgicalMember.param_group_member_options.map((opt) => ({
                value: opt.option_value.toLowerCase(),
                label: opt.option_value,
                param_group_member_id: opt.irm_param_group_members_id,
                param_group_member_option_id: opt.id,
                param_group_id: surgicalMember.irm_param_groups_id,
              })),
            );
          } else {
            setOpdParams([]);
          }
        }

        resetSurgical(defaultSurgicalValues);

        await loadContactHistory(rawDonor.id);
      } catch (err) {
        console.error('Error loading donor data:', err);
        Swal.fire({ icon: 'error', title: 'Load Failed', text: err?.response?.data?.message || 'Failed to load donor data' });
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [donorCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save generic history
  const onGenericSubmit = async (data) => {
    if (!donorInfo?.id) return;
    setSaving(true);
    try {
      // Delete cleared fields
      for (const orig of originalGenericHistories) {
        const currentVal = data[orig.fieldName];
        const isBlank = !currentVal || currentVal.trim() === '' || currentVal.trim() === '<p></p>' || currentVal.trim() === '<p><br></p>';
        if (orig.value && isBlank) {
          await api.opd.deleteContactHistory({
            contact_id: orig.contact_id,
            param_group_id: orig.param_group_id,
            param_group_member_id: orig.param_group_member_id,
            contact_history_id: orig.id,
          });
        }
      }

      // Build payload for non-blank fields
      // We need param_group info from genericParams; map via slug
      const paramsRes = await api.opd.getHistoryParams();
      const genericGroup = paramsRes?.data?.history_params;
      const members = genericGroup?.param_group_members || [];

      const payload = [];
      Object.entries(GENERIC_FIELD_SLUGS).forEach(([field, slug]) => {
        const val = data[field];
        const isBlank = !val || val.trim() === '' || val.trim() === '<p></p>' || val.trim() === '<p><br></p>';
        if (!isBlank) {
          const member = members.find((m) => m.slug === slug);
          if (member) {
            payload.push({
              contact_id: donorInfo.id,
              param_group_id: genericGroup.id,
              param_group_member_id: member.id,
              param_group_member_option_id: null,
              name: member.name,
              value: val,
              history_date: null,
              remarks: null,
              private_note: null,
            });
          }
        }
      });

      if (payload.length > 0) {
        await api.opd.createContactHistory(payload);
      }

      Swal.fire({ icon: 'success', title: 'Saved', text: 'Generic history saved successfully!', timer: 2000, showConfirmButton: false });
      await loadContactHistory(donorInfo.id);
    } catch (err) {
      console.error('Error saving generic history:', err);
      Swal.fire({ icon: 'error', title: 'Save Failed', text: err?.response?.data?.message || 'Failed to save history' });
    } finally {
      setSaving(false);
    }
  };

  // Save surgical history entry
  const onSurgicalSubmit = async (data) => {
    if (!donorInfo?.id || !data.surgicalHistories?.length) {
      Swal.fire({ icon: 'warning', title: 'Missing Field', text: 'Please add at least one surgical record' });
      return;
    }
    const payload = [];
    for (const entry of data.surgicalHistories) {
      if (!entry.surgery) continue;
      const selected = opdParams.find((o) => o.value === entry.surgery.toLowerCase());
      if (!selected) continue;
      payload.push({
        contact_id: donorInfo.id,
        param_group_id: selected.param_group_id || surgicalMember?.irm_param_groups_id || 3,
        param_group_member_id: selected.param_group_member_id || surgicalMember?.id || 10,
        param_group_member_option_id: selected.param_group_member_option_id || null,
        name: surgicalMember?.name || 'Surgical History',
        value: selected.label,
        history_date: entry.history_date || null,
        remarks: entry.notes || null,
        private_note: null,
      });
    }
    if (!payload.length) {
      Swal.fire({ icon: 'warning', title: 'Missing Field', text: 'Please fill valid surgical records' });
      return;
    }
    setSaving(true);
    try {
      await api.opd.createContactHistory(payload);
      Swal.fire({ icon: 'success', title: 'Saved', text: 'Surgical history added!', timer: 1500, showConfirmButton: false });
      resetSurgical(defaultSurgicalValues);
      await loadContactHistory(donorInfo.id);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Save Failed', text: err?.response?.data?.message || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSurgical = async (record) => {
    const confirm = await Swal.fire({
      icon: 'warning',
      title: 'Delete?',
      text: `Remove surgical history: ${record.surgery}?`,
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    });
    if (!confirm.isConfirmed) return;

    try {
      await api.opd.deleteContactHistory({
        contact_id: record.contact_id,
        param_group_id: record.param_group_id,
        param_group_member_id: record.param_group_member_id,
        contact_history_id: record.id,
      });
      Swal.fire({ icon: 'success', title: 'Deleted!', timer: 1200, showConfirmButton: false });
      await loadContactHistory(donorInfo.id);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Delete Failed', text: err?.response?.data?.message || 'Failed to delete' });
    }
  };

  const handleEditSurgical = (record) => {
    setEditingSurgicalRecord({
      ...record,
      notes: record.notes || '',
      history_date: record.history_date || '',
      param_group_member_option_id: record.param_group_member_option_id || opdParams.find((opt) => opt.label === record.surgery)?.param_group_member_option_id || null,
    });
  };

  const handleUpdateSurgical = async () => {
    if (!editingSurgicalRecord?.id) return;

    const selectedOption = opdParams.find(
      (opt) => opt.param_group_member_option_id === editingSurgicalRecord.param_group_member_option_id,
    ) || opdParams.find((opt) => opt.label === editingSurgicalRecord.surgery);

    if (!selectedOption) {
      Swal.fire({ icon: 'warning', title: 'Missing Field', text: 'Please select a valid surgery type' });
      return;
    }

    setSaving(true);
    try {
      await api.opd.updateContactHistory({
        contact_history_id: editingSurgicalRecord.id,
        value: selectedOption.label,
        history_date: editingSurgicalRecord.history_date || null,
        remarks: editingSurgicalRecord.notes || null,
        param_group_member_id: selectedOption.param_group_member_id || editingSurgicalRecord.param_group_member_id,
        param_group_member_option_id: selectedOption.param_group_member_option_id,
      });

      Swal.fire({ icon: 'success', title: 'Updated', text: 'Surgical history updated successfully', timer: 1500, showConfirmButton: false });
      setEditingSurgicalRecord(null);
      await loadContactHistory(donorInfo.id);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Update Failed', text: err?.response?.data?.message || 'Failed to update' });
    } finally {
      setSaving(false);
    }
  };

  const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: '7px', fontSize: '0.9rem' } };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <LoaderOverlay open={loading} />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              {/* Header */}
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="logo"
                borderRadius="lg"
                coloredShadow="logo"
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={2}>
                    <IconButton
                      onClick={() => navigate('/donor-list')}
                      sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                    <MDTypography variant="h6" color="white">
                      Contact Histories – {donorInfo?.name || donorCode}
                    </MDTypography>
                  </Box>
                  <Tooltip title="Investigations">
                    <IconButton
                      onClick={() => navigate(`/donor-investigations/${donorCode}`)}
                      sx={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.3)',
                        width: 40,
                        height: 40,
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                      }}
                    >
                      <ScienceIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </MDBox>

              <MDBox p={3}>
                {donorInfo && (
                  <Box mb={2}>
                    <Chip label={`Donor: ${donorInfo.external_code}`} size="small" sx={{ bgcolor: logoColor, color: '#fff', fontWeight: 600 }} />
                    <Chip
                      label={donorInfo.gender === 'M' ? 'Male' : 'Female'}
                      size="small"
                      sx={{ ml: 1, bgcolor: donorInfo.gender === 'M' ? '#1976d2' : '#e91e8c', color: '#fff', fontWeight: 600 }}
                    />
                  </Box>
                )}

                {/* Generic History Accordion */}
                <Accordion
                  expanded={expandedAccordion === 'generic'}
                  onChange={(_, isExpanded) => setExpandedAccordion(isExpanded ? 'generic' : false)}
                  sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: '8px !important', '&:before': { display: 'none' } }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#f8f9fa', borderRadius: '8px' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <HistoryIcon sx={{ color: logoColor, fontSize: 20 }} />
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ color: logoColor }}>
                        Generic History
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <form onSubmit={handleGenericSubmit(onGenericSubmit)}>
                      <Grid container spacing={2}>
                        {[
                          { field: 'patient_history', label: 'Previous History' },
                          { field: 'family_history', label: 'Family History' },
                          { field: 'allergic_to_drugs', label: 'Allergic to Drugs' },
                          { field: 'drug_medical_history', label: 'Drug / Medical History' },
                          { field: 'previous_infertility_treatment', label: 'Previous Infertility Treatment' },
                          { field: 'notes', label: 'Notes' },
                        ].map(({ field, label }) => (
                          <Grid item xs={12} key={field}>
                            <Typography variant="caption" fontWeight="bold" display="block" mb={0.5} sx={{ color: '#555' }}>
                              {label}
                            </Typography>
                            <Controller
                              name={field}
                              control={genericControl}
                              render={({ field: f }) => (
                                <RichTextEditor
                                  value={f.value}
                                  onChange={f.onChange}
                                  placeholder={`Enter ${label.toLowerCase()}…`}
                                />
                              )}
                            />
                          </Grid>
                        ))}
                      </Grid>
                      <Box display="flex" justifyContent="flex-end" mt={2}>
                        <button
                          type="submit"
                          disabled={saving}
                          style={{
                            padding: '10px 24px',
                            background: saving ? '#cccccc' : logoColor,
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            cursor: saving ? 'not-allowed' : 'pointer',
                            fontSize: '1rem',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <SaveIcon fontSize="small" />
                          {saving ? 'Saving…' : 'Save Generic History'}
                        </button>
                      </Box>
                    </form>
                  </AccordionDetails>
                </Accordion>

                {/* Surgical History Accordion */}
                <Accordion
                  expanded={expandedAccordion === 'surgical'}
                  onChange={(_, isExpanded) => setExpandedAccordion(isExpanded ? 'surgical' : false)}
                  sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: '8px !important', '&:before': { display: 'none' } }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#f8f9fa', borderRadius: '8px' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <HistoryIcon sx={{ color: logoColor, fontSize: 20 }} />
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ color: logoColor }}>
                        Surgical History
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {/* Existing surgical records */}
                    <Box mb={3}>
                      <Typography variant="subtitle2" fontWeight="bold" mb={1} sx={{ color: logoColor }}>
                        Existing Surgical Records
                      </Typography>
                      <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          overflow: 'hidden',
                          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
                          border: '1px solid #e7edf3',
                        }}
                      >
                        <Table size="small">
                          <TableBody>
                            <TableRow sx={{ bgcolor: '#f8fbfc' }}>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: logoColor, py: 2 }}>Surgery</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: logoColor, py: 2 }}>Surgery Date</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: logoColor, py: 2 }}>Notes</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem', color: logoColor, py: 2, width: 90 }}>
                                Actions
                              </TableCell>
                            </TableRow>
                            {previousSurgicalData.length > 0 ? (
                              previousSurgicalData.map((rec) => {
                                const isEditing = editingSurgicalRecord?.id === rec.id;

                                return (
                                  <TableRow
                                    key={rec.id}
                                    hover
                                    sx={{
                                      '&:last-child td': { borderBottom: 0 },
                                      '& td': { verticalAlign: 'top', py: 1.75 },
                                      bgcolor: isEditing ? '#fff8e1' : 'inherit',
                                    }}
                                  >
                                    <TableCell sx={{ fontSize: '0.9rem', color: '#344054', fontWeight: 500, minWidth: 180 }}>
                                      {isEditing ? (
                                        <FormControl fullWidth size="small" sx={{ ...inputSx, '& .MuiInputBase-root': { minHeight: 40 } }}>
                                          <Select
                                            value={editingSurgicalRecord.param_group_member_option_id || ''}
                                            onChange={(e) => {
                                              const selected = opdParams.find((opt) => opt.param_group_member_option_id === e.target.value);
                                              setEditingSurgicalRecord((prev) => ({
                                                ...prev,
                                                surgery: selected?.label || '',
                                                param_group_member_id: selected?.param_group_member_id || prev.param_group_member_id,
                                                param_group_member_option_id: selected?.param_group_member_option_id || null,
                                              }));
                                            }}
                                            displayEmpty
                                          >
                                            {opdParams.map((option) => (
                                              <MenuItem key={option.param_group_member_option_id} value={option.param_group_member_option_id}>
                                                {option.label}
                                              </MenuItem>
                                            ))}
                                          </Select>
                                        </FormControl>
                                      ) : rec.surgery || '—'}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.88rem', color: '#475467', whiteSpace: 'nowrap', minWidth: 170 }}>
                                      {isEditing ? (
                                        <MDDatePicker
                                          label="Surgery Date"
                                          value={editingSurgicalRecord.history_date || ''}
                                          onChange={(value) => setEditingSurgicalRecord((prev) => ({ ...prev, history_date: value || '' }))}
                                          textFieldProps={{
                                            fullWidth: true,
                                            size: 'small',
                                            placeholder: 'dd/mm/yyyy',
                                            sx: {
                                              width: '100%',
                                              '& .MuiOutlinedInput-root': { borderRadius: '7px', fontSize: '0.9rem', minHeight: 40 },
                                            },
                                          }}
                                        />
                                      ) : formatDateForDisplay(rec.history_date) || '—'}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.88rem', color: '#344054', minWidth: 260 }}>
                                      {isEditing ? (
                                        <RichTextEditor
                                          value={editingSurgicalRecord.notes || ''}
                                          onChange={(value) => setEditingSurgicalRecord((prev) => ({ ...prev, notes: value || '' }))}
                                          placeholder="Optional notes"
                                        />
                                      ) : (
                                        <Box
                                          sx={{
                                            minWidth: 220,
                                            maxWidth: 420,
                                            color: '#344054',
                                            '& p': { margin: 0 },
                                            '& p + p': { marginTop: '6px' },
                                            '& ul, & ol': { margin: '4px 0', paddingLeft: '20px' },
                                            '& li': { marginBottom: '2px' },
                                            '& em': { color: '#98a2b3' },
                                          }}
                                          dangerouslySetInnerHTML={{ __html: rec.notes || '<em>—</em>' }}
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      {isEditing ? (
                                        <>
                                          <Tooltip title="Save changes">
                                            <IconButton size="small" color="success" onClick={handleUpdateSurgical} disabled={saving}>
                                              <SaveIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Cancel edit">
                                            <IconButton size="small" onClick={() => setEditingSurgicalRecord(null)} disabled={saving}>
                                              <CloseIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </>
                                      ) : (
                                        <>
                                          <Tooltip title="Edit record">
                                            <IconButton size="small" color="warning" onClick={() => handleEditSurgical(rec)}>
                                              <EditIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Delete record">
                                            <IconButton size="small" color="error" onClick={() => handleDeleteSurgical(rec)}>
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            ) : (
                              <TableRow>
                                <TableCell
                                  colSpan={4}
                                  align="center"
                                  sx={{
                                    color: '#98a2b3',
                                    fontStyle: 'italic',
                                    py: 4,
                                    fontSize: '1rem',
                                  }}
                                >
                                  No previous records found.
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Divider sx={{ my: 2 }} />
                    </Box>

                    {/* Add new surgical entry */}
                    <Typography variant="subtitle2" fontWeight="bold" mb={1.5} sx={{ color: logoColor }}>
                      Add New Surgical Record
                    </Typography>
                    <form onSubmit={handleSurgicalSubmit(onSurgicalSubmit)}>
                      {surgicalFields.map((item, idx) => (
                        <Grid container spacing={2} key={item.id} alignItems="flex-start" sx={{ mb: 1.5 }}>
                          <Grid item xs={12} sm={4}>
                            <Controller
                              name={`surgicalHistories.${idx}.surgery`}
                              control={surgicalControl}
                              render={({ field }) => (
                                <FormControl fullWidth size="small" sx={{ ...inputSx, '& .MuiInputBase-root': { minHeight: 40 } }}>
                                  <Select
                                    {...field}
                                    displayEmpty
                                    value={field.value || ''}
                                    disabled={opdParams.length === 0}
                                    sx={{ minHeight: 40 }}
                                    renderValue={(value) => {
                                      if (!value) {
                                        return <span style={{ color: '#9e9e9e' }}>Select surgery type *</span>;
                                      }
                                      return opdParams.find((opt) => opt.value === value)?.label || value;
                                    }}
                                  >
                                    <MenuItem value="" disabled>
                                      Select surgery type *
                                    </MenuItem>
                                    {opdParams.map((option) => (
                                      <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Controller
                              name={`surgicalHistories.${idx}.history_date`}
                              control={surgicalControl}
                              render={({ field }) => (
                                <Box sx={{ width: '100%' }}>
                                  <MDDatePicker
                                    label="Surgery Date"
                                    value={field.value || ''}
                                    onChange={(value) => field.onChange(value || '')}
                                    textFieldProps={{
                                      fullWidth: true,
                                      size: 'small',
                                      placeholder: 'dd/mm/yyyy',
                                      sx: {
                                        width: '100%',
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: '7px',
                                          fontSize: '0.9rem',
                                          minHeight: 40,
                                        },
                                      },
                                    }}
                                  />
                                </Box>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Controller
                              name={`surgicalHistories.${idx}.notes`}
                              control={surgicalControl}
                              render={({ field }) => (
                                <RichTextEditor
                                  value={field.value}
                                  onChange={field.onChange}
                                  placeholder="Optional notes"
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 40 }}>
                            <Tooltip title={surgicalFields.length === 1 ? 'At least one row is required' : 'Remove row'}>
                              <span>
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => removeSurgical(idx)}
                                  disabled={surgicalFields.length === 1}
                                  sx={{ mt: { xs: 0, sm: 0.5 } }}
                                >
                                  <RemoveCircleOutlineIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Grid>
                        </Grid>
                      ))}
                      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} mt={2} gap={2}>
                        <Tooltip title="Add another surgical row">
                          <span>
                            <IconButton
                              color="primary"
                              onClick={() => appendSurgical({ surgery: '', notes: '', history_date: '' })}
                              disabled={saving}
                              sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                            >
                              <AddCircleOutlineIcon fontSize="large" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={saving}
                          startIcon={<SaveIcon fontSize="small" />}
                          sx={{
                            minWidth: 220,
                            height: 42,
                            px: 2.5,
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontSize: '0.95rem',
                            fontWeight: 700,
                            letterSpacing: 0,
                            color: '#ffffff',
                            backgroundColor: saving ? '#9fb7bd' : logoColor,
                            boxShadow: 'none',
                            border: '1px solid transparent',
                            '&:hover': {
                              backgroundColor: saving ? '#9fb7bd' : '#006577',
                              boxShadow: 'none',
                            },
                            '&:active': {
                              boxShadow: 'none',
                            },
                            '&.Mui-disabled': {
                              color: '#ffffff',
                              backgroundColor: '#b7c9cd',
                            },
                          }}
                        >
                          {saving ? 'Saving…' : 'Add Surgical Record(s)'}
                        </Button>
                      </Box>
                    </form>
                  </AccordionDetails>
                </Accordion>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default DonorContactHistories;
